import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  GenericInput,
  Icon,
  InputType,
  StyledTableScroll,
  type Tab,
  Tabs,
  tabButtonId,
  tabPanelId,
} from "@studio3t/generic-components";
import {
  type Column,
  type ColumnFiltersState,
  createColumnHelper,
  type FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type SortingFn,
  type SortingState,
  type Table as TableInstance,
  useReactTable,
} from "@tanstack/react-table";
import {
  type ElementType,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { useDropdownDismissGuard } from "@/components/dropdownDismissGuard";
import { EmptyState } from "@/components/EmptyState";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import {
  ActionsCell,
  BadgeSlot,
  CellText,
  EmptyRowMessage,
  PageRoot,
  Table,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ListTable";
import { PipelineFlowCell } from "@/components/PipelineFlowCell";
import {
  BreadcrumbCurrent,
  PipelinesHeader,
} from "@/components/PipelinesHeader";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import { SortableHeader as SharedSortableHeader } from "@/components/SortableHeader";
import {
  FilterBar,
  FilterControls,
  FilterInputWrap,
  FilterMultiSelect,
} from "@/components/TableFilters";
import { TablePagination } from "@/components/TablePagination";
import { useToast } from "@/components/toast/ToastProvider";
import {
  useDeleteJob,
  useJobs,
  useRestartJob,
  useStopJob,
  useWorkspaces,
} from "@/hooks/useApi";
import { useDuplicateJob, useImportJob } from "@/hooks/useImportJob";
import { UndeliveredNotice, undeliveredRecords } from "@/pages/pipelineBacklog";
import type { JobMetadata, Workspace } from "@/types/api";
import { userStorage } from "@/utils/userStorage";
import {
  DUPLICATE_PIPELINE_MODAL,
  duplicateName,
  SaveWorkspaceModal,
} from "./NewStudio/SaveWorkspaceModal";
import { DraftsTable } from "./PipelineDraftsTable";
import { JobActions, useJobAction } from "./PipelineJobActions";
import { deriveJobControlState } from "./pipelineControls";
import { draftWorkspaces } from "./pipelineDrafts";
import {
  formatPipeline,
  formatTimeAgo,
  normalizeState,
  sortByStatusPriority,
} from "./pipelineHelpers";

const TABLE = designVariables.ui.table;
const SEMANTIC = designVariables.ui.semantic;

// The page's two lists (Figma AM_Tabs 14894-157313): the deployed jobs and
// the draft workspaces. The active one lives in the URL (?tab=drafts) so a
// refresh or a shared link lands on the same list.
type PipelinesTab = "deployed" | "drafts";
const TABS_ID_BASE = "pipelines";

function usePipelinesTab(): [PipelinesTab, (tab: PipelinesTab) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: PipelinesTab =
    searchParams.get("tab") === "drafts" ? "drafts" : "deployed";
  const setTab = (next: PipelinesTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === "drafts") params.set("tab", "drafts");
    else params.delete("tab");
    setSearchParams(params);
  };
  return [tab, setTab];
}

// The drafts count once both lists are in; undefined while either loads.
function draftsCount(
  workspaces: Workspace[] | undefined,
  jobs: JobMetadata[] | undefined,
): number | undefined {
  if (!workspaces || !jobs) return undefined;
  return draftWorkspaces(workspaces, jobs).length;
}

// Each tab wears its count once the list is in; nothing while it loads.
function countBadge(count: number | undefined) {
  return count === undefined ? undefined : (
    <BadgeSecondarySubtle>{count}</BadgeSecondarySubtle>
  );
}

function PipelineTabs({
  tab,
  onChange,
  deployedCount,
  draftsCount,
}: {
  tab: PipelinesTab;
  onChange: (tab: PipelinesTab) => void;
  deployedCount: number | undefined;
  draftsCount: number | undefined;
}) {
  const tabs: Tab[] = [
    { id: "deployed", label: "Deployed", badge: countBadge(deployedCount) },
    { id: "drafts", label: "Drafts", badge: countBadge(draftsCount) },
  ];
  return (
    <TabsRow>
      <Tabs
        tabs={tabs}
        activeId={tab}
        onChange={(id) => onChange(id as PipelinesTab)}
        label="Pipeline lists"
        idBase={TABS_ID_BASE}
      />
    </TabsRow>
  );
}

// The tab's focus ring is drawn outside its box (see the library's tab
// tokens), so its top stroke lies in the 4px band above the row — inside the
// pinned page header's box, which paints at z-index 10 and hid it. The row
// stacks above the header instead. Safe here because this page never scrolls
// as a whole: PageRoot is viewport-high and the table scrolls internally, so
// the row never crosses the header the way scrolling content would.
const TabsRow = styled.div`
  position: relative;
  z-index: 11;
  flex-shrink: 0;
  margin-bottom: ${SEMANTIC.spacingm}px;
`;

// The active list, wired to its tab for assistive tech; lays out like the
// page root so the table card can scroll internally.
const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// Adapts a TanStack column to the shared SortableHeader control. Status sorts
// by lifecycle priority, not alphabetically, so its arrow tracks that order
// rather than A→Z.
function SortableHeader({
  column,
  children,
}: {
  column: Column<JobMetadata, unknown>;
  children: ReactNode;
}) {
  return (
    <SharedSortableHeader
      direction={column.getIsSorted() || null}
      onClick={() => column.toggleSorting()}
    >
      {children}
    </SharedSortableHeader>
  );
}

// Mirrors the column's sort state onto the <th> so assistive tech can announce
// whether (and how) a column is sorted; undefined leaves aria-sort off the
// non-sortable columns entirely.
function ariaSortFor(
  column: Column<JobMetadata, unknown>,
): "ascending" | "descending" | "none" | undefined {
  if (!column.getCanSort()) return undefined;
  const sorted = column.getIsSorted();
  if (sorted === "asc") return "ascending";
  if (sorted === "desc") return "descending";
  return "none";
}

const PipelineName = styled(CellText)``;

// Width comes from the colgroup (fixed table layout); no extra styles needed.
const StatusCell = styled(TableCell)``;

type StatusFilter = "Running" | "Stopped" | "Failed";
type CheckpointFilter = "enabled" | "off";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "Running", label: "Running" },
  { value: "Stopped", label: "Stopped" },
  { value: "Failed", label: "Failed" },
];

const CHECKPOINT_OPTIONS: { value: CheckpointFilter; label: string }[] = [
  { value: "enabled", label: "Enabled" },
  { value: "off", label: "Off" },
];

const PAGE_SIZES = [10, 25, 50, 100];

const columnHelper = createColumnHelper<JobMetadata>();

// Multi-select column filter: empty/absent selection matches everything.
const includesAnyOf: FilterFn<JobMetadata> = (row, columnId, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  return (filterValue as string[]).includes(row.getValue<string>(columnId));
};

// Chronological sort for ISO/date-string columns; the default alphanumeric
// sort would order them as text. NaN dates (shouldn't occur) sort as 0.
const sortByDateString: SortingFn<JobMetadata> = (rowA, rowB, columnId) => {
  const a = new Date(rowA.getValue<string>(columnId)).getTime();
  const b = new Date(rowB.getValue<string>(columnId)).getTime();
  return (Number.isNaN(a) ? 0 : a) - (Number.isNaN(b) ? 0 : b);
};

// Columns whose <td> differs from the default TableCell.
const CELL_COMPONENTS: Record<string, ElementType> = {
  status: StatusCell,
  actions: ActionsCell,
};

// Filter and page-size settings survive a page refresh (per user).
const TABLE_SETTINGS_KEY = "pipelines-table";

interface StoredTableSettings {
  name?: string;
  status?: StatusFilter[];
  checkpoint?: CheckpointFilter[];
  pageSize?: number;
  sort?: SortingState;
}

// Column ids that support sorting (the "actions" column is excluded). Guards
// against stale storage referencing other columns.
const SORTABLE_COLUMN_IDS = [
  "name",
  "pipeline",
  "status",
  "checkpoint",
  "started_at",
];

function parseSort(value: unknown): SortingState | undefined {
  if (!Array.isArray(value)) return undefined;
  const sort = value.filter(
    (s): s is { id: string; desc: boolean } =>
      typeof s === "object" &&
      s !== null &&
      typeof (s as { id?: unknown }).id === "string" &&
      SORTABLE_COLUMN_IDS.includes((s as { id: string }).id) &&
      typeof (s as { desc?: unknown }).desc === "boolean",
  );
  return sort.length ? sort : undefined;
}

// Keeps only values that are still valid options (storage may hold stale data).
function parseFilterValues<T extends string>(
  value: unknown,
  options: { value: T; label: string }[],
): T[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const allowed = options.map((o) => o.value);
  return value.filter((v): v is T => allowed.includes(v as T));
}

function loadTableSettings(): StoredTableSettings {
  try {
    const raw = userStorage.getItem(TABLE_SETTINGS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    const stored = parsed as Record<string, unknown>;
    return {
      name: typeof stored.name === "string" ? stored.name : undefined,
      status: parseFilterValues(stored.status, STATUS_OPTIONS),
      checkpoint: parseFilterValues(stored.checkpoint, CHECKPOINT_OPTIONS),
      pageSize: PAGE_SIZES.includes(stored.pageSize as number)
        ? (stored.pageSize as number)
        : undefined,
      sort: parseSort(stored.sort),
    };
  } catch {
    return {};
  }
}

function saveTableSettings(settings: StoredTableSettings): void {
  userStorage.setItem(TABLE_SETTINGS_KEY, JSON.stringify(settings));
}

function initialColumnFilters(stored: StoredTableSettings): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  if (stored.name) filters.push({ id: "name", value: stored.name });
  if (stored.status?.length) {
    filters.push({ id: "status", value: stored.status });
  }
  if (stored.checkpoint?.length) {
    filters.push({ id: "checkpoint", value: stored.checkpoint });
  }
  return filters;
}

// Keep the page in range when the row set shrinks underneath it — polling
// removes pipelines (autoResetPageIndex is off), or the count drops to <=10 and
// the pagination bar (which carries its own clamp) stops rendering. Without this
// the body would ask for a now-empty page and show nothing.
function useClampedPageIndex(
  table: TableInstance<JobMetadata>,
  pageIndex: number,
) {
  const pageCount = table.getPageCount();
  useEffect(() => {
    const maxIndex = Math.max(0, pageCount - 1);
    if (pageIndex > maxIndex) table.setPageIndex(maxIndex);
  }, [table, pageCount, pageIndex]);
}

// A single body row: opens the pipeline on click/Enter/Space, and stops the
// actions cell from bubbling that navigation.
function PipelineTableRow({
  row,
  onRowClick,
}: {
  row: Row<JobMetadata>;
  onRowClick: (name: string) => void;
}) {
  return (
    <TableRow
      tabIndex={0}
      onClick={() => onRowClick(row.original.name)}
      onKeyDown={(e) => {
        // Keydown bubbles from focusable cell content (e.g. the actions dropdown
        // trigger); only act on the row itself.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row.original.name);
        }
      }}
    >
      {row.getVisibleCells().map((cell) => {
        const Cell = CELL_COMPONENTS[cell.column.id] ?? TableCell;
        return (
          <Cell
            key={cell.id}
            onClick={
              cell.column.id === "actions"
                ? (e: ReactMouseEvent) => e.stopPropagation()
                : undefined
            }
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Cell>
        );
      })}
    </TableRow>
  );
}

// Skeleton body shown while the jobs query loads. Uses the same row/cell
// components as the data rows so column widths and padding match exactly; the
// bar shapes echo each column's content (name, flow, badge, text, text, —).
const SKELETON_ROW_COUNT = 8;
const SKELETON_ROWS = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, i) => `skeleton-${i}`,
);

function SkeletonBodyRows() {
  return (
    <>
      {SKELETON_ROWS.map((key) => (
        <TableRow key={key} aria-hidden="true">
          <TableCell>
            <SkeletonBar $width="70%" />
          </TableCell>
          <TableCell>
            <SkeletonBar $width="55%" />
          </TableCell>
          <StatusCell>
            <SkeletonPill $width="64px" />
          </StatusCell>
          <TableCell>
            <SkeletonBar $width="60%" />
          </TableCell>
          <TableCell>
            <SkeletonBar $width="60%" />
          </TableCell>
          <ActionsCell />
        </TableRow>
      ))}
    </>
  );
}

function PipelinesTable({
  jobs,
  loading,
  handleRowClick,
  handleStop,
  handleRestart,
  handleDelete,
  importJob,
  duplicateJob,
  stopping,
  restarting,
  deleting,
  importing,
}: {
  jobs: JobMetadata[];
  // True while the jobs query is loading: the body renders skeleton rows
  // instead of data rows (which don't exist yet).
  loading: boolean;
  handleRowClick: (name: string) => void;
  handleStop: (name: string) => void;
  handleRestart: (name: string) => void;
  handleDelete: (name: string) => void;
  importJob: (job: JobMetadata) => void;
  duplicateJob: (job: JobMetadata) => void;
  stopping: boolean;
  restarting: boolean;
  deleting: boolean;
  importing: boolean;
}) {
  const [stored] = useState(loadTableSettings);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
    initialColumnFilters(stored),
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: stored.pageSize ?? 10,
  });
  const [sorting, setSorting] = useState<SortingState>(() => stored.sort ?? []);
  // A click that closes a row menu or a filter menu does nothing else.
  const dismissGuard = useDropdownDismissGuard<HTMLDivElement>();
  const onMenuOpenChange = dismissGuard.onDropdownOpenChange;

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        filterFn: "includesString",
        cell: (info) => <PipelineName>{info.getValue()}</PipelineName>,
      }),
      // Sortable by source (service.resource) only — the flow's natural sort
      // key. Middleware/sink are intentionally not sort options (a composite
      // flow can't be meaningfully sorted by a single rotating arrow).
      columnHelper.accessor((job) => formatPipeline(job).source, {
        id: "pipeline",
        header: "Pipeline",
        cell: (info) => <PipelineFlowCell job={info.row.original} />,
      }),
      columnHelper.accessor(
        (job) =>
          deriveJobControlState(
            normalizeState(job.state),
            normalizeState(job.desired_state),
          ).displayState,
        {
          id: "status",
          header: "Status",
          filterFn: includesAnyOf,
          sortingFn: sortByStatusPriority,
          cell: (info) => (
            <BadgeSlot>
              <JobStatusBadge state={info.getValue()} />
            </BadgeSlot>
          ),
        },
      ),
      columnHelper.accessor(
        (job) => (job.pipeline?.checkpoint?.enabled ? "enabled" : "off"),
        {
          id: "checkpoint",
          header: "Checkpoint",
          filterFn: includesAnyOf,
          cell: (info) => (
            <CellText>
              {info.getValue() === "enabled" ? "Enabled" : "Off"}
            </CellText>
          ),
        },
      ),
      columnHelper.accessor("started_at", {
        header: "Started",
        sortingFn: sortByDateString,
        cell: (info) => <CellText>{formatTimeAgo(info.getValue())}</CellText>,
      }),
      columnHelper.display({
        id: "actions",
        cell: (info) => (
          <JobActions
            job={info.row.original}
            state={normalizeState(info.row.original.state)}
            onStop={handleStop}
            onRestart={handleRestart}
            onDelete={handleDelete}
            onImport={importJob}
            onDuplicate={duplicateJob}
            onMenuOpenChange={onMenuOpenChange}
            stopping={stopping}
            restarting={restarting}
            deleting={deleting}
            importing={importing}
          />
        ),
      }),
    ],
    [
      handleStop,
      handleRestart,
      handleDelete,
      importJob,
      duplicateJob,
      onMenuOpenChange,
      stopping,
      restarting,
      deleting,
      importing,
    ],
  );

  const table = useReactTable({
    data: jobs,
    columns,
    state: { columnFilters, pagination, sorting },
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: (updater) => {
      setSorting(updater);
      // Sorting reorders the whole set; jump back to the first page so the new
      // top rows are in view (autoResetPageIndex is off for polling).
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (job) => job.name,
    // Polling hands the table a new data array every few seconds; the default
    // auto-reset would yank the user back to page 0 on every poll.
    autoResetPageIndex: false,
  });

  const nameFilter =
    (table.getColumn("name")?.getFilterValue() as string) ?? "";
  const statusFilter =
    (table.getColumn("status")?.getFilterValue() as StatusFilter[]) ?? [];
  const checkpointFilter =
    (table.getColumn("checkpoint")?.getFilterValue() as CheckpointFilter[]) ??
    [];

  useEffect(() => {
    const filterValue = (id: string) =>
      columnFilters.find((f) => f.id === id)?.value;
    saveTableSettings({
      name: (filterValue("name") as string) ?? "",
      status: (filterValue("status") as StatusFilter[]) ?? [],
      checkpoint: (filterValue("checkpoint") as CheckpointFilter[]) ?? [],
      pageSize: pagination.pageSize,
      sort: sorting,
    });
  }, [columnFilters, pagination.pageSize, sorting]);

  const total = table.getFilteredRowModel().rows.length;
  useClampedPageIndex(table, pagination.pageIndex);

  const setFilter = (columnId: string, value: unknown) => {
    table.getColumn(columnId)?.setFilterValue(value);
    table.setPageIndex(0);
  };

  return (
    <TableCard
      ref={dismissGuard.containerRef}
      onClickCapture={dismissGuard.onClickCapture}
    >
      <FilterBar>
        <FilterInputWrap>
          <GenericInput
            type={InputType.Text}
            value={nameFilter}
            placeholder="Filter by name"
            onChange={(e) => setFilter("name", e.target.value || undefined)}
            allowClear={nameFilter.length > 0}
            onClear={() => setFilter("name", undefined)}
          />
        </FilterInputWrap>
        <FilterControls>
          <FilterMultiSelect
            placeholder="Status"
            values={statusFilter}
            options={STATUS_OPTIONS}
            onChange={(v) => setFilter("status", v.length ? v : undefined)}
            onOpenChange={onMenuOpenChange}
          />
          <FilterMultiSelect
            placeholder="Checkpoint"
            values={checkpointFilter}
            options={CHECKPOINT_OPTIONS}
            onChange={(v) => setFilter("checkpoint", v.length ? v : undefined)}
            onOpenChange={onMenuOpenChange}
          />
        </FilterControls>
      </FilterBar>

      <StyledTableScroll>
        <Table>
          {/* 240/120 mirror Figma's Table cell "middle width"/"min width"
              variables, which @studio3t/design-variables doesn't export yet. */}
          <colgroup>
            <col style={{ width: 240 }} />
            <col />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: TABLE.fixwidth }} />
          </colgroup>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeader
                    key={header.id}
                    aria-sort={ariaSortFor(header.column)}
                    aria-label={
                      header.column.id === "actions" ? "Actions" : undefined
                    }
                  >
                    {header.column.getCanSort() ? (
                      <SortableHeader column={header.column}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </SortableHeader>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </TableHeader>
                ))}
              </tr>
            ))}
          </TableHead>
          <tbody>
            {loading && <SkeletonBodyRows />}
            {!loading &&
              table
                .getRowModel()
                .rows.map((row) => (
                  <PipelineTableRow
                    key={row.id}
                    row={row}
                    onRowClick={handleRowClick}
                  />
                ))}
          </tbody>
        </Table>

        {!loading && total === 0 && (
          <EmptyRowMessage>
            {jobs.length === 0
              ? "You have not added any pipelines yet"
              : "No pipelines match your filters"}
          </EmptyRowMessage>
        )}
      </StyledTableScroll>

      {/* Only page once there are more than a full page (10) of pipelines —
          10 → no bar, 11 → bar. Threshold is the default page size, so the bar
          can't vanish after the user picks a larger one. The page-clamp lives in
          the effect above so it runs even when the bar is hidden. */}
      {total > PAGE_SIZES[0] && (
        <TablePagination
          pageIndex={pagination.pageIndex}
          pageCount={table.getPageCount()}
          pageSize={pagination.pageSize}
          total={total}
          onPageChange={(index) => table.setPageIndex(index)}
          onPageSizeChange={(size) => {
            table.setPageSize(size);
            table.setPageIndex(0);
          }}
          onOpenChange={onMenuOpenChange}
        />
      )}
    </TableCard>
  );
}

// Delete flow: a request opens the confirmation modal; confirm performs the
// API call and keeps the modal open with an error message on failure. A
// pipeline holding undelivered records is refused by the engine; the modal
// then asks again, naming what a delete would discard, and only that second
// confirmation deletes them.
function useDeletePipeline(
  deleteJob: (vars: {
    name: string;
    discardBacklog?: boolean;
  }) => Promise<unknown>,
) {
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [undelivered, setUndelivered] = useState<number | null>(null);

  const close = useCallback(() => {
    setPendingName(null);
    setError(null);
    setUndelivered(null);
  }, []);

  // useDeleteJob invalidates the jobs query (and useJobs polls), so the list
  // refreshes without a manual refetch here.
  const confirm = useCallback(async () => {
    if (!pendingName) return;
    setError(null);
    try {
      await deleteJob({
        name: pendingName,
        discardBacklog: undelivered !== null,
      });
      close();
    } catch (err) {
      const records = undeliveredRecords(err);
      if (records !== undefined) {
        setUndelivered(records);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Failed to delete pipeline",
      );
    }
  }, [pendingName, undelivered, deleteJob, close]);

  return {
    pendingName,
    request: setPendingName,
    confirm,
    close,
    error,
    undelivered,
  };
}

// Confirms a delete. Once the engine has refused it for undelivered records,
// the same dialog says what a delete would discard and asks again.
function DeletePipelineModal({
  del,
  deleting,
}: {
  del: ReturnType<typeof useDeletePipeline>;
  deleting: boolean;
}) {
  const name = <strong>{del.pendingName}</strong>;
  const discards = del.undelivered !== null;
  return (
    <ConfirmDeleteModal
      isOpen={del.pendingName !== null}
      title="Delete pipeline"
      message={
        del.undelivered === null ? (
          <>
            Are you sure you want to delete {name}? This action cannot be
            undone.
          </>
        ) : (
          <UndeliveredNotice
            subject={name}
            action="Deleting it"
            records={del.undelivered}
          />
        )
      }
      confirmLabel={discards ? "Delete and discard" : "Delete"}
      loading={deleting}
      error={del.error}
      onConfirm={del.confirm}
      onCancel={del.close}
    />
  );
}

// The active list under the tabs: the deployed jobs table with its
// lifecycle actions, or the drafts table.
function PipelineListPanel({
  tab,
  jobs,
  workspaces,
  deployed,
}: {
  tab: PipelinesTab;
  jobs: JobMetadata[] | undefined;
  workspaces: Workspace[] | undefined;
  deployed: Omit<Parameters<typeof PipelinesTable>[0], "jobs" | "loading">;
}) {
  return (
    <TabPanel
      role="tabpanel"
      id={tabPanelId(TABS_ID_BASE, tab)}
      aria-labelledby={tabButtonId(TABS_ID_BASE, tab)}
    >
      {tab === "deployed" ? (
        // While the jobs query loads, the table shows skeleton rows.
        <PipelinesTable jobs={jobs ?? []} loading={!jobs} {...deployed} />
      ) : (
        <DraftsTable
          workspaces={workspaces ?? []}
          jobs={jobs ?? []}
          loading={!workspaces || !jobs}
        />
      )}
    </TabPanel>
  );
}

function Pipelines() {
  const navigate = useNavigate();
  const { data: jobs, error } = useJobs();
  const { data: workspaces } = useWorkspaces();
  const [tab, setTab] = usePipelinesTab();
  const { mutateAsync: stopJob, isPending: stopping } = useStopJob();
  const { mutateAsync: restartJob, isPending: restarting } = useRestartJob();
  const { mutateAsync: deleteJob, isPending: deleting } = useDeleteJob();
  const {
    importJob,
    loading: importing,
    error: importError,
    dismissError: dismissImportError,
  } = useImportJob();

  const duplicate = useDuplicateJob();
  const stop = useJobAction(stopJob, "Failed to stop pipeline");
  const restart = useJobAction(restartJob, "Failed to start pipeline");
  const del = useDeletePipeline(deleteJob);

  // An import failure surfaces as an error toast (stop/start failures do
  // so inside useJobAction); the hook's own state is cleared right after so
  // the same failure can toast again.
  const { toast } = useToast();
  useEffect(() => {
    if (!importError) return;
    toast({ state: "Error", message: importError });
    dismissImportError();
  }, [importError, toast, dismissImportError]);

  const handleRowClick = useCallback(
    (name: string) => {
      navigate(`/pipelines/${encodeURIComponent(name)}`);
    },
    [navigate],
  );

  if (error) {
    return (
      <EmptyState
        icon={Icon.Warning}
        title="Failed to load pipelines"
        description={error.message}
      />
    );
  }

  return (
    <PageRoot>
      <PipelinesHeader
        count={jobs?.length}
        breadcrumb={<BreadcrumbCurrent>Pipelines list</BreadcrumbCurrent>}
      />

      <PipelineTabs
        tab={tab}
        onChange={setTab}
        deployedCount={jobs?.length}
        draftsCount={draftsCount(workspaces, jobs)}
      />

      <PipelineListPanel
        tab={tab}
        jobs={jobs}
        workspaces={workspaces}
        deployed={{
          handleRowClick,
          handleStop: stop.execute,
          handleRestart: restart.execute,
          handleDelete: del.request,
          importJob,
          duplicateJob: duplicate.request,
          stopping,
          restarting,
          deleting,
          importing,
        }}
      />

      {duplicate.pending !== null && (
        <SaveWorkspaceModal
          isOpen
          onClose={duplicate.close}
          onSave={duplicate.duplicate}
          {...DUPLICATE_PIPELINE_MODAL}
          initialName={duplicateName(duplicate.pending.name)}
        />
      )}

      <DeletePipelineModal del={del} deleting={deleting} />
    </PageRoot>
  );
}

export default Pipelines;
